import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../config/firebase';
import { motion } from 'framer-motion';
import './TechnicalArticles.css';
import { applyPageSeo, setHtmlLang, setJsonLd, removeJsonLd, SITE_NAME } from '../utils/seo';

interface Article {
  title: string;
  content: string;
  description?: string;
  author: string;
  date: string;
}

const TechnicalArticles: React.FC = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<{ [key: string]: Article }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // SEO for the listing page: title/description/canonical/OG + hreflang.
  useEffect(() => {
    const prev = document.title;
    setHtmlLang(i18n.language);
    applyPageSeo({
      title: t('technicalArticles.pageTitle'),
      description: t('technicalArticles.description'),
      path: '/technical-articles',
      image: '/logo512.png'
    });
    return () => { document.title = prev; };
  }, [t, i18n.language]);

  // Structured data for the listing. Runs after the articles load so the
  // ItemList reflects what is actually on the page.
  useEffect(() => {
    const entries = Object.entries(articles);
    if (!entries.length) return;
    const origin = window.location.origin;
    setJsonLd('articles-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: t('technicalArticles.pageTitle'),
      description: t('technicalArticles.description'),
      url: `${origin}/technical-articles`,
      inLanguage: i18n.language,
      isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: origin },
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: entries.length,
        itemListElement: entries.map(([key, article], index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${origin}/technical-articles/${key}`,
          name: article.title
        }))
      }
    });
    return () => removeJsonLd('articles-jsonld');
  }, [articles, t, i18n.language]);

  useEffect(() => {
    const currentLang = i18n.language === 'ru' ? 'ru' : i18n.language === 'ar' ? 'ar' : 'en';
    const articlesRef = ref(database, `technicalArticles/${currentLang}`);

    onValue(
      articlesRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setArticles(data);
        } else {
          setArticles({});
        }
        setLoading(false);
        setError('');
      },
      (err) => {
        setError(t('technicalArticles.loadError'));
        setLoading(false);
        console.error('Error fetching articles:', err);
      }
    );

    return () => {
      off(articlesRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable from i18next
  }, [i18n.language]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      let locale = 'en-US';
      if (i18n.language === 'ru') locale = 'ru-RU';
      else if (i18n.language === 'ar') locale = 'ar-SA';
      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const articlesArray = Object.entries(articles).map(([id, article]) => ({
    id,
    ...article,
  }));

  // Tarihe göre sırala (en yeni önce)
  articlesArray.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  if (loading) {
    return (
      <div className="technical-articles-page">
        <section className="articles-hero">
          <div className="articles-hero-content">
            <h1>{t('technicalArticles.pageTitle')}</h1>
            <p className="articles-hero-subtitle">
              {t('technicalArticles.subtitle')}
            </p>
          </div>
        </section>
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t('technicalArticles.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="technical-articles-page">
        <section className="articles-hero">
          <div className="articles-hero-content">
            <h1>{t('technicalArticles.pageTitle')}</h1>
            <p className="articles-hero-subtitle">
              {t('technicalArticles.subtitle')}
            </p>
          </div>
        </section>
        <div className="container">
          <div className="error-state">
            <div className="error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h3>{t('technicalArticles.error')}</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="technical-articles-page">
      {/* Hero Section */}
      <section className="articles-hero">
        <div className="articles-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="articles-hero-text"
          >
            <h1>{t('technicalArticles.pageTitle')}</h1>
            <p className="articles-hero-subtitle">
              {t('technicalArticles.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="articles-section">
        <div className="container">
          {articlesArray.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="no-articles-state"
            >
              <div className="no-articles-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <h3>{t('technicalArticles.noArticles')}</h3>
              <p>{t('technicalArticles.noArticlesDesc')}</p>
            </motion.div>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="articles-header"
              >
                <h2 className="articles-count">
                  {articlesArray.length} {articlesArray.length === 1 ? t('technicalArticles.article') : t('technicalArticles.articles')}
                </h2>
                <p className="articles-intro">
                  {t('technicalArticles.intro')}
                </p>
              </motion.div>

              <div className="articles-grid">
                {articlesArray.map((article, index) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="article-card"
                  >
                    <div className="article-card-content">
                      <div className="article-card-header">
                        <div className="article-category">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          <span>{t('technicalArticles.category')}</span>
                        </div>
                        <h2>{article.title}</h2>
                      </div>
                      
                      {article.description && (
                        <div className="article-card-description">
                          {article.description}
                        </div>
                      )}

                      <div className="article-card-meta">
                        <div className="article-meta-item">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          <span>{formatDate(article.date)}</span>
                        </div>
                        {article.author && (
                          <div className="article-meta-item">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                              <circle cx="12" cy="7" r="4"/>
                            </svg>
                            <span>{article.author}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="read-more-button"
                      onClick={() => navigate(`/technical-articles/${article.id}`)}
                    >
                      {t('technicalArticles.readMore')}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default TechnicalArticles;
